/* Scrollbar compensation for Dawn cart drawer + console logs.
   Uses a BASELINE scrollbar width measured once on page load.
   This avoids measuring after the drawer toggles overflow (which can yield 0).
*/
(() => {
  const SCROLLER = () => document.scrollingElement || document.documentElement;
  const DIR = () => (document.documentElement.dir || document.dir || 'ltr').toLowerCase();
  const SIDE = () => (DIR() === 'rtl' ? 'left' : 'right');
  const CAP = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const px = (n) => `${n}px`;
  const toNum = (v) => (parseFloat(v || '0') || 0);
  const log = (...args) => console.log('[atd-cart-drawer]', ...args);

  // --- BASELINE SCROLLBAR WIDTH (measured once on load; updated only when safe)
  let BASE_SBW = 0;
  function measureBaselineSBW() {
    // Use viewport minus layout width (fast + accurate)
    const w = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    BASE_SBW = w;
    // log('baseline sbw measured', BASE_SBW);
  }

  // track active drawers + applied state
  const activeDrawers = new Set();
  let applied = false;

  // snapshot of inline styles for exact restore
  const saved = {
    overflow: '',
    padLeft: '',
    padRight: '',
    fixedPads: new Map(),
  };

  function applyComp() {
    if (applied) return;

    const width = BASE_SBW; // <-- use baseline value
    const side = SIDE();
    const scroller = SCROLLER();

    saved.overflow  = scroller.style.overflow;
    saved.padLeft   = scroller.style.paddingLeft;
    saved.padRight  = scroller.style.paddingRight;

    if (width > 0) {
      const current = toNum(getComputedStyle(scroller).getPropertyValue(`padding-${side}`));
      scroller.style[`padding${CAP(side)}`] = px(current + width);

      document.querySelectorAll('[data-avoid-shift]').forEach((el) => {
        const prevInline = el.style[`padding${CAP(side)}`] || '';
        const cur = toNum(getComputedStyle(el).getPropertyValue(`padding-${side}`));
        el.style[`padding${CAP(side)}`] = px(cur + width);
        saved.fixedPads.set(el, prevInline);
      });
    }

    applied = true;
    log('opening → applied compensation', { side, sbw: width });
  }

  function clearComp() {
    if (!applied) return;

    const side = SIDE();
    const scroller = SCROLLER();
    scroller.style.overflow    = saved.overflow || '';
    scroller.style.paddingLeft = saved.padLeft || '';
    scroller.style.paddingRight= saved.padRight || '';

    saved.fixedPads.forEach((prevInline, el) => {
      el.style[`padding${CAP(side)}`] = prevInline || '';
    });
    saved.fixedPads.clear();

    applied = false;
    log('closed → cleared compensation');
  }

  function refresh() {
    if (applied) clearComp();
    if (activeDrawers.size > 0) applyComp();
  }

  function onDrawerStateChange() {
    if (activeDrawers.size > 0) {
      if (!applied) log('drawer state → active (will open)');
      refresh();
    } else {
      if (applied) log('drawer state → inactive (will close)');
      clearComp();
    }
  }

  function watchDrawer(el) {
    if (el.classList.contains('active')) activeDrawers.add(el);

    const mo = new MutationObserver((mut) => {
      for (const m of mut) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          const wasActive = activeDrawers.has(el);
          const nowActive = el.classList.contains('active');
          if (nowActive) {
            activeDrawers.add(el);
            if (!wasActive) log('<cart-drawer> became active', el);
          } else {
            if (wasActive) log('<cart-drawer> lost active', el);
            activeDrawers.delete(el);
          }
          onDrawerStateChange();
        }
      }
    });
    mo.observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  function scanAndWatch() {
    document.querySelectorAll('cart-drawer').forEach(watchDrawer);
  }

  const treeObs = new MutationObserver((mut) => {
    for (const m of mut) {
      m.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;
        if (n.matches?.('cart-drawer')) watchDrawer(n);
        n.querySelectorAll?.('cart-drawer')?.forEach(watchDrawer);
      });
      m.removedNodes.forEach((n) => {
        if (n instanceof Element && activeDrawers.has(n)) {
          log('<cart-drawer> removed while active', n);
          activeDrawers.delete(n);
          onDrawerStateChange();
        }
      });
    }
  });

  // Early custom events (optional, from cart.js)
  window.addEventListener('atd:cart:drawer:will-open', () => {
    log('event: will-open');
    if (!applied) applyComp();
  });
  window.addEventListener('atd:cart:drawer:did-open', () => {
    log('event: did-open');
    refresh();
  });
  window.addEventListener('atd:cart:drawer:did-close', () => {
    log('event: did-close');
    clearComp();
  });

  // Recalculate baseline ONLY when it's safe (no active drawers),
  // so we don’t sample after overflow hidden changed layout.
  window.addEventListener('resize', () => {
    if (activeDrawers.size === 0) {
      measureBaselineSBW();
      log('resize → new baseline sbw', BASE_SBW);
    } else {
      log('resize during open → keep baseline', BASE_SBW);
      refresh(); // keep compensation using existing baseline
    }
  });

  function init() {
    measureBaselineSBW();     // <-- measure once, upfront
    scanAndWatch();
    treeObs.observe(document.documentElement, { childList: true, subtree: true });
    onDrawerStateChange();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Debug helpers
  window.ATD_CART_DRAWER_PAD = {
    baseline: () => BASE_SBW,
    applied: () => applied,
    activeCount: () => activeDrawers.size,
    refresh,
    clear: clearComp,
    _measureNow: () => { measureBaselineSBW(); return BASE_SBW; }
  };
})();
